/**
 * Adds a document pane to the data entry views (AP/PO), shown beside the header fields.
 *
 * The pane opens automatically only when the route carries a `proscan_log` query param, i.e.
 * the transaction was reached from a Proscan extraction. Opening a transaction directly
 * leaves it closed; the Show File toggle in the data entry buttons fetches the transaction's
 * documents on demand and only opens the pane if there are any.
 */
import { notify } from '@/utils/helpers/Notify'
import { DateFormatter } from '@/utils/helpers/FormatDate'

export const proscanFileViewMixins = {
  data () {
    return {
      proscanLog: null,
      proscanFileVisible: false,
      proscanFiles: [],
      proscanFilesLoaded: false,
      loadingProscanFiles: false
    }
  },
  computed: {
    /**
     * The extraction log id this transaction was opened with, if any
     * @return {string|null}
     */
    proscanLogId () {
      return this.$route.query && this.$route.query.proscan_log ? this.$route.query.proscan_log : null
    },
    /**
     * Whether this view was reached from a Proscan extraction
     * @return {boolean}
     */
    isProscanTransaction () {
      return !!this.proscanLogId
    },
    /**
     * The extracted document, preselected in the pane when coming from Proscan
     * @return {string|null}
     */
    proscanFileId () {
      return this.proscanLog && this.proscanLog.file_data ? this.proscanLog.file_data.id : null
    },
    /**
     * True when the document pane should be rendered
     * @return {boolean}
     */
    showProscanFile () {
      return this.proscanFileVisible && !!this.proscanFiles.length
    },
    /**
     * API path of the transaction being edited, used to list its documents, e.g. 'aps/12'
     * @return {string}
     */
    proscanTransactionPath () {
      const type = this.transactionType && this.transactionType.path
        ? this.transactionType.path.replace(/^\/|\/$/g, '')
        : ''
      return type && this.$route.params.id ? `${type}/${this.$route.params.id}` : ''
    }
  },
  methods: {
    /**
     * Loads the extraction log, so the document it was run against can be preselected
     * @return {Promise<void>}
     */
    async loadProscanLog () {
      if (!this.proscanLogId) return
      this.proscanLog = await this.$store.dispatch('fetchProscanLogById', this.proscanLogId)
    },
    /**
     * Loads the transaction's documents. The extracted document is listed first even if the
     * attachment list hasn't caught up with it yet.
     * @return {Promise<Array>}
     */
    async loadProscanFiles () {
      if (!this.proscanTransactionPath) return []
      this.loadingProscanFiles = true
      try {
        const results = await this.$store.dispatch('fetchTransactionFiles', this.proscanTransactionPath)
        const rows = results.map(file => ({
          id: file.id,
          name: file.original_filename || file.name,
          uploadedOn: file.created_on ? DateFormatter.formatISODateString(file.created_on) : ''
        }))
        if (this.proscanFileId && !rows.some(row => row.id === this.proscanFileId)) {
          rows.unshift({
            id: this.proscanFileId,
            name: (this.proscanLog.file_data && this.proscanLog.file_data.name) || this.proscanLog.file_name || '',
            uploadedOn: ''
          })
        }
        this.proscanFiles = rows
        this.proscanFilesLoaded = true
        return rows
      } finally {
        this.loadingProscanFiles = false
      }
    },
    /**
     * Show/hide the document pane. Opening it fetches the transaction's documents the first
     * time, and stays closed with a notice when there is nothing to preview.
     * @return {Promise<void>}
     */
    async toggleProscanFile () {
      if (this.proscanFileVisible) {
        this.proscanFileVisible = false
        return
      }
      if (!this.proscanFilesLoaded) await this.loadProscanFiles()
      if (!this.proscanFiles.length) {
        notify(this, 'warning', 'No files to show a preview for. Upload one with the Files button.')
        return
      }
      this.proscanFileVisible = true
    },
    /**
     * Re-reads the document list after a file is uploaded through the Files dialog, so the
     * pane picks it up without a page reload
     * @return {Promise<void>}
     */
    async refreshProscanFiles () {
      await this.loadProscanFiles()
    }
  },
  async mounted () {
    if (!this.proscanLogId) return
    await this.loadProscanLog()
    await this.loadProscanFiles()
    this.proscanFileVisible = !!this.proscanFiles.length
  }
}



// WEBPACK FOOTER //
// ./src/Mixins/ProscanFileView.js